import { Button, Col, Input, Radio, Row, Typography, Spin } from "antd";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../../App.css";
import "../../index.css";
import {
  handleGoToNextStep,
  handleUpdateErrorsOfOrderCreamForm,
  handleUpdatePainManagement,
  handleGoBack,
  saveStep8Api,
  getOrderByTransactionIdApi,
} from "../../redux-toolkit/slices/orderCreamSlice";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import { validateOrderCreamPainManagement } from "../../schemas/OrderCream";
import ErrorList from "../Items/ErrorsList";
import NewEntryFormLeftColWrapper from "../wrappers/NewEntryFormLeftColWrapper";
import NewEntryFormRightColWrapper from "../wrappers/NewEntryFormRightColWrapper";

const OcPainmanagementGoals = () => {
  const orderCreamForm = useSelector((state: RootState) => state.orderCream);
  const { painManagement, isError, errors, transactionId, currentStep, isLoading } = orderCreamForm;
  const { painGoal, timing, applicationFrequency, usedBefore, experience } =
    painManagement;
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  // Fetch order data when component mounts
  useEffect(() => {
    const fetchOrder = async () => {
      if (transactionId) {
        setIsLoadingOrder(true);
        try {
          const orderData = await dispatch(
            getOrderByTransactionIdApi(transactionId)
          ).unwrap();
          // The Redux reducer will automatically populate the state with order data
        } catch (error) {
          console.error("Error loading order:", error);
        } finally {
          setIsLoadingOrder(false);
        }
      }
    };
    
    fetchOrder();
  }, [transactionId, dispatch]);

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      const newParams = new URLSearchParams(searchParams);
      newParams.set("step", prevStep.toString());
      setSearchParams(newParams, { replace: true });
      // Don't call handleGoBack() - let the parent component sync from URL
    }
  };

  const handleTimingChange = (e: any) => {
    dispatch(
      handleUpdatePainManagement({ key: "timing", value: e.target.value })
    );
  };

  const handlePainGoalChange = (e: any) => {
    dispatch(
      handleUpdatePainManagement({ key: "painGoal", value: e.target.value })
    );
  };

  const handleApplicationFrequencyChange = (e: any) => {
    dispatch(
      handleUpdatePainManagement({
        key: "applicationFrequency",
        value: e.target.value,
      })
    );
  };

  const handleUsedBeforeChange = (e: any) => {
    dispatch(
      handleUpdatePainManagement({
        key: "usedBefore",
        value: e.target.value,
      })
    );
  };

  const handleExperienceChange = (e: any) => {
    dispatch(
      handleUpdatePainManagement({
        key: "experience",
        value: e.target.value,
      })
    );
  };
  const handleSubmit = async () => {
    // Validate radio buttons first - check for valid values
    const validTiming = ["am", "afternoon", "evening", "allday", "varies"];
    const validPainGoal = ["strength", "dailytasks", "activities", "medication", "sleep", "other"];
    const validApplicationFrequency = ["multiple", "once", "asneeded"];
    const validUsedBefore = ["yes", "no"];

    if (!timing || 
        timing === "" || 
        timing === null || 
        timing === undefined ||
        (typeof timing === 'string' && timing.trim() === "") ||
        !validTiming.includes(timing)) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose timing."],
        })
      );
    }

    if (!painGoal || 
        painGoal === "" || 
        painGoal === null || 
        painGoal === undefined ||
        (typeof painGoal === 'string' && painGoal.trim() === "") ||
        !validPainGoal.includes(painGoal)) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose pain goal."],
        })
      );
    }

    if (!applicationFrequency || 
        applicationFrequency === "" || 
        applicationFrequency === null || 
        applicationFrequency === undefined ||
        (typeof applicationFrequency === 'string' && applicationFrequency.trim() === "") ||
        !validApplicationFrequency.includes(applicationFrequency)) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose application frequency."],
        })
      );
    }

    if (!usedBefore || 
        usedBefore === "" || 
        usedBefore === null || 
        usedBefore === undefined ||
        (typeof usedBefore === 'string' && usedBefore.trim() === "") ||
        !validUsedBefore.includes(usedBefore)) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose if you have used before."],
        })
      );
    }

    // Remove _id before validation
    const { _id, ...painManagementWithoutId } = painManagement as any;
    const { isError, errors } =
      validateOrderCreamPainManagement(painManagementWithoutId);

    if (isError) {
      return dispatch(handleUpdateErrorsOfOrderCreamForm({ isError, errors }));
    }

    if (usedBefore === "yes" && experience.trim() === "") {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please describe your experience"],
        })
      );
    }

    if (!transactionId) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Transaction ID is missing. Please refresh the page."],
        })
      );
    }

    try {
      // Sanitize painManagement to remove _id fields
      const sanitizedPainManagement = {
        timing: painManagement.timing,
        painGoal: painManagement.painGoal,
        applicationFrequency: painManagement.applicationFrequency,
        usedBefore: painManagement.usedBefore,
        experience: painManagement.experience || '',
      };

      await dispatch(
        saveStep8Api({
          transactionId,
          painManagement: sanitizedPainManagement,
        })
      ).unwrap();

      // Update URL with new step
      const newParams = new URLSearchParams(searchParams);
      newParams.set("step", "9");
      setSearchParams(newParams, { replace: true });

      dispatch(handleGoToNextStep());
    } catch (error) {
      dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Failed to save step. Please try again."],
        })
      );
    }
  };

  return (
    <div className="pain-management-goals-entry">
      <div className="flex flex-col gap-5 w-full">
        {/* Pain Timing Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>When do you typically experience the most pain or discomfort during the day? *</strong>
          </label>
          <Radio.Group onChange={handleTimingChange} value={timing}>
            <Radio value="am">Early Morning</Radio>
            <Radio value="afternoon">Midday/Afternoon</Radio>
            <Radio value="evening">Evening/Night</Radio>
            <Radio value="allday">All day</Radio>
            <Radio value="varies">It varies (please explain)</Radio>
          </Radio.Group>
        </div>

        {/* Pain Management Goals Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>What are your primary goals for pain management? (Select all that apply) *</strong>
          </label>
          <Radio.Group onChange={handlePainGoalChange} value={painGoal}>
            <Radio value="strength">
              To improve my strength and flexibility
            </Radio>
            <Radio value="dailytasks">
              To be able to perform daily tasks currently limited by my pain
              (e.g., cooking, cleaning)
            </Radio>
            <Radio value="activities">
              To return to physical activities or hobbies (e.g., walking,
              gardening, sports)
            </Radio>
            <Radio value="medication">
              To reduce reliance on pain medication
            </Radio>
            <Radio value="sleep">To improve my sleep quality</Radio>
            <Radio value="other">Other (please specify)</Radio>
          </Radio.Group>
        </div>

        {/* Application Frequency Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>How do you prefer to apply topical treatments? *</strong>
          </label>
          <Radio.Group
            onChange={handleApplicationFrequencyChange}
            value={applicationFrequency}
          >
            <Radio value="multiple">Multiple times a day</Radio>
            <Radio value="once">Once daily</Radio>
            <Radio value="asneeded">As needed</Radio>
          </Radio.Group>
        </div>

        {/* Previous Experience Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>Have you used similar topical treatments before? *</strong>
          </label>
          <Radio.Group onChange={handleUsedBeforeChange} value={usedBefore}>
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
          {usedBefore === "yes" && (
            <Input.TextArea
              placeholder="Please specify the formula and describe your experience like effectiveness, side effects"
              rows={4}
              value={experience}
              onChange={handleExperienceChange}
              className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-all duration-200 bg-white custom-placeholder"
              style={{ marginTop: "10px" }}
            />
          )}
        </div>

        {/* Error Display */}
        {isError === true && (
          <div className="mt-6">
            <ErrorList errors={errors} />
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-center gap-4" style={{ marginTop: "40px" }}>
          <button
            onClick={handleBack}
            className="caption2 transition-colors duration-200"
            style={{ color: 'var(--primary-500)', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Previous
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-lg"
            disabled={isLoading}
            style={{ position: 'relative', minWidth: '120px' }}
          >
            {isLoading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Spin size="small" />
                Saving...
              </span>
            ) : (
              'Next'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OcPainmanagementGoals;
